// ModifyUserModal.jsx – même look-and-feel que ModifyAppelOffreModal
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Paper,
  Modal,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllUsersForDropdown } from "../../redux/usersSlice";

const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#4a6bad";
const OUTLINE    = "#D4D9E0";
const LABEL_GREY = "#4F5565";

const GradientPaper = styled(Paper)({
  elevation: 8,
  borderRadius: 3,
  padding: "24px",
  width: "100%",
  maxWidth: 820,
  margin: "auto",
  background: "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
  position: "relative",
});

const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
});

export default function ModifyUserModal({ open, handleClose, user, updateUser }) {
  const dispatch = useDispatch();
  const { allUsers, currentUser } = useSelector((s) => s.users);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const [formData, setFormData] = useState({
    username:  "",
    email:     "",
    roles:     [],
    rules:     "",
    isPremium: false,
    companyId: "",
  });

  useEffect(() => {
    if (allUsers.length === 0) dispatch(fetchAllUsersForDropdown());
  }, [dispatch, allUsers.length]);

  useEffect(() => {
    if (open && user) {
      setFormData({
        username:  user.username  || "",
        email:     user.email     || "",
        roles:     Array.isArray(user.roles) ? user.roles : [],
        rules:     user.rules     || "",
        isPremium: !!user.isPremium,
        companyId: user.company?.id||"",
      });
    }
  }, [open, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };
  const handleRoleChange = (e) =>
    setFormData((s) => ({ ...s, roles: [e.target.value] }));

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      await updateUser(user.id, {
        ...formData,
        isPremium: formData.isPremium ? 1 : 0,
      });
      handleClose();
    } catch {
      setError("Erreur lors de la mise à jour.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" }, // Reduced opacity
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", sm: 820 },
          bgcolor: "transparent",
          boxShadow: "none",
        }}
      >
        <GradientPaper>
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 16, right: 16, color: LABEL_GREY }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h5"
            align="center"
            sx={{ fontWeight: 700, color: NAVY, mb: 3 }}
          >
            Modifier l’utilisateur
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
              gap: 2,
            }}
          >
            <CustomTextField
              label="Nom d'utilisateur"
              name="username"
              value={formData.username}
              onChange={handleChange}
              fullWidth
            />
            <CustomTextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Rôle</InputLabel>
              <Select
                name="roles"
                value={formData.roles[0]||""}
                label="Rôle"
                onChange={handleRoleChange}
              >
                {["ROLE_ADMIN","ROLE_USER","ROLE_COLLABORATOR"].map(r=>(
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Droits</InputLabel>
              <Select
                name="rules"
                value={formData.rules}
                label="Droits"
                onChange={handleChange}
              >
                {["read","write"].map(r=>(
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Abonnement</InputLabel>
              <Select
                name="isPremium"
                value={formData.isPremium}
                label="Abonnement"
                onChange={handleChange}
              >
                <MenuItem value={false}>Standard</MenuItem>
                <MenuItem value={true}>Premium</MenuItem>
              </Select>
            </FormControl>

            {currentUser && (
              <FormControl fullWidth>
                <InputLabel>Société</InputLabel>
                <Select
                  name="companyId"
                  value={formData.companyId}
                  label="Société"
                  onChange={handleChange}
                >
                  <MenuItem value="">–</MenuItem>
                  {allUsers.filter(u=>u.roles.includes("ROLE_ADMIN"))
                    .map(u=>(
                      <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: NAVY,
                "&:hover": { backgroundColor: NAVY_LIGHT },
                px: 5,
                fontWeight: "bold",
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#FFF" }} />
              ) : (
                "Modifier"
              )}
            </Button>
          </Box>
        </GradientPaper>
      </Box>
    </Modal>
  );
}
